<script setup lang="ts">
import { defineAsyncComponent, reactive, ref } from 'vue'
import { createEmptyAnswers, type CurriculumTopic, type JudgeResult } from '../../lib/issue_diagnosis/types'
import IssueDiagnosisIntro from './IssueDiagnosisIntro.vue'

// 初回表示（ランチャーボタン）に不要な質問データ・判定ロジック・後続画面は
// ボタン押下後まで読み込みを遅延させ、初期バンドルを軽量に保つ。
const IssueDiagnosisWizard = defineAsyncComponent(() => import('./IssueDiagnosisWizard.vue'))
const IssueDiagnosisLeadForm = defineAsyncComponent(() => import('./IssueDiagnosisLeadForm.vue'))
const IssueDiagnosisProposal = defineAsyncComponent(() => import('./IssueDiagnosisProposal.vue'))

const props = defineProps<{
  curriculumTopics: CurriculumTopic[]
  contactUrl: string
  inflowRoute: string
}>()

type Screen = 'closed' | 'intro' | 'wizard' | 'judging' | 'leadForm' | 'proposal'

const screen = ref<Screen>('closed')
const answers = reactive(createEmptyAnswers())
const result = ref<JudgeResult | null>(null)
const diagnosisToken = ref('')
const reportUrl = ref('')

function open() {
  screen.value = 'intro'
}

function close() {
  screen.value = 'closed'
  Object.assign(answers, createEmptyAnswers())
  result.value = null
  diagnosisToken.value = ''
  reportUrl.value = ''
}

function startWizard() {
  screen.value = 'wizard'
}

function backToIntro() {
  screen.value = 'intro'
}

// Q1〜Q14の回答完了時点でレコードを作成する。会社名等の入力有無に関わらず、
// ここで作成しておくことで「診断は完了したが連絡先を送信しなかった」離脱を計測できる。
async function completeWizard() {
  screen.value = 'judging'
  const [{ judge }, { buildSummary, buildBottleneck }, { createDiagnosis }] = await Promise.all([
    import('../../lib/issue_diagnosis/judge'),
    import('../../lib/issue_diagnosis/content'),
    import('../../lib/issue_diagnosis/submit'),
    new Promise<void>((resolve) => window.setTimeout(resolve, 800)),
  ])

  const judged = judge(answers, props.curriculumTopics)
  result.value = judged

  const hasM1 = judged.modules.includes('M1' as never)
  const summary = buildSummary(answers)
  const bottleneck = buildBottleneck(judged.type, hasM1)

  try {
    const created = await createDiagnosis(judged, answers, summary, bottleneck, props.inflowRoute)
    diagnosisToken.value = created.token
  } catch (e) {
    diagnosisToken.value = ''
  }

  screen.value = 'leadForm'
}

function handleSubmitted(url: string) {
  reportUrl.value = url
  screen.value = 'proposal'
}
</script>

<template>
  <div class="issue_diagnosis_root">
    <div class="issue_diagnosis_launcher_wrap">
      <p class="issue_diagnosis_launcher_bubble">
        <span class="issue_diagnosis_launcher_bubble_full">2分でわかる<br>マーケティング課題診断</span>
        <span class="issue_diagnosis_launcher_bubble_short">課題診断</span>
      </p>
      <button type="button" class="issue_diagnosis_launcher" aria-label="2分でわかるマーケティング課題診断をはじめる" @click="open">
        <span class="issue_diagnosis_launcher_pulse" aria-hidden="true"></span>
        <svg class="issue_diagnosis_launcher_icon" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M4 4.5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-4.4 3.6a.6.6 0 0 1-.98-.46V18.5H4a2 2 0 0 1-2-2v-10a2 2 0 0 1 2-2z"
            fill="currentColor"
          />
          <circle cx="8.4" cy="11" r="1.3" fill="#fff" />
          <circle cx="12" cy="11" r="1.3" fill="#fff" />
          <circle cx="15.6" cy="11" r="1.3" fill="#fff" />
        </svg>
      </button>
    </div>

    <div v-if="screen !== 'closed'" class="issue_diagnosis_overlay">
      <div class="issue_diagnosis_modal" :class="{ issue_diagnosis_modal_wide: screen === 'proposal' }">
        <button type="button" class="issue_diagnosis_close" aria-label="閉じる" @click="close">×</button>

        <IssueDiagnosisIntro v-if="screen === 'intro'" @start="startWizard" />

        <IssueDiagnosisWizard
          v-else-if="screen === 'wizard'"
          :answers="answers"
          @complete="completeWizard"
          @exit="backToIntro"
        />

        <div v-else-if="screen === 'judging'" class="issue_diagnosis_judging">
          <p>診断中…</p>
        </div>

        <IssueDiagnosisLeadForm
          v-else-if="screen === 'leadForm' && result"
          :token="diagnosisToken"
          @submitted="handleSubmitted"
        />

        <IssueDiagnosisProposal
          v-else-if="screen === 'proposal' && result"
          :result="result"
          :answers="answers"
          :contact-url="contactUrl"
          :report-url="reportUrl"
        />
      </div>
    </div>
  </div>
</template>
